import type { PricingPlan } from '@/features/pricing-plans/api/pricing-plans';

/**
 * Pure pricing math for the contract wizard. All money is bigint **tiyin**
 * (1 UZS = 100 tiyin) — never floats (invariant #1). Area is multiplied by 100
 * ("centisqm") so fractional m² survive integer arithmetic.
 *
 * Two surcharge modes (see `PricingPlan.surchargeMode`):
 *   flat            → surcharge added to every m²: gross = (cashPerSqm + surchargePerSqm) × area.
 *                     Default down payment is `downPaymentPercent` of the gross.
 *   annual_financed → surchargePerSqm is tiyin/m²/YEAR on the financed (unpaid) m² only:
 *                     surcharge = (area − downPayment/cashPerSqm) × surchargePerSqm × (termMonths/12).
 *                     Default down payment is `downPaymentPercent` of the BASE (cashPerSqm × area),
 *                     because the surcharge itself depends on the down payment.
 *
 * A contract DISCOUNT is subtracted from the COST PRICE (base = cashPerSqm × area).
 * For annual_financed the discount lowers the EFFECTIVE per-m² price, so the financed
 * (unpaid) m² — and therefore the surcharge — are computed on the discounted price:
 *   discountedBase = base − discount
 *   financedM²     = area − downPayment / (discountedBase / area)
 *   surcharge      = financedM² × surchargePerSqm × (termMonths/12)
 *   total          = discountedBase + surcharge
 * For flat the surcharge is a fixed per-m² add-on, so it is unchanged by the discount:
 *   total = (base + surchargePerSqm × area) − discount
 * The schedule generator later computes monthly = (total − down) / installmentCount.
 */

export type PlanTotals = {
  baseTiyin: bigint; // cashPerSqm × area (cost price / tannarx, before discount)
  downTiyin: bigint; // down payment used (override, else percent-derived per mode)
  surchargeTiyin: bigint; // mode-dependent surcharge (discount-independent)
  totalTiyin: bigint; // (base − discount) + surcharge
};

export function computePlanTotals(params: {
  pricePerSqmTiyin: bigint;
  areaSqm: number;
  plan: Pick<PricingPlan, 'downPaymentPercent' | 'surchargePerSqm' | 'surchargeMode' | 'termMonths'>;
  /** When the agent overrides the down payment (e.g. a fixed 150 mln), pass it here in tiyin. */
  downOverrideTiyin?: bigint;
  /** Contract discount in tiyin, subtracted from the cost price (base). */
  discountTiyin?: bigint;
}): PlanTotals {
  const { pricePerSqmTiyin, areaSqm, plan, downOverrideTiyin, discountTiyin = 0n } = params;
  const areaCentisqm = BigInt(Math.round(areaSqm * 100));
  const surchargePerSqm = BigInt(plan.surchargePerSqm);
  const pctBp = BigInt(Math.round(Number(plan.downPaymentPercent) * 100)); // basis points
  const baseTiyin = (pricePerSqmTiyin * areaCentisqm) / 100n;

  if (plan.surchargeMode === 'annual_financed') {
    // Discount lowers the effective price; the financed m² is measured against it.
    const discountedBaseTiyin = baseTiyin - discountTiyin;
    // Down first (percent of the discounted price), because surcharge depends on financed m².
    const downTiyin = downOverrideTiyin ?? (discountedBaseTiyin * pctBp) / 10_000n;
    // m² the down covers at the effective (discounted) price = down / (discountedBase / area).
    // With discount=0 this reduces to down × 100 / pricePerSqm (the list price).
    const downCentisqm =
      discountedBaseTiyin > 0n ? (downTiyin * areaCentisqm) / discountedBaseTiyin : 0n;
    const financedCentisqm = areaCentisqm > downCentisqm ? areaCentisqm - downCentisqm : 0n;
    // financedCentisqm/100 × surchargePerSqm × termMonths/12 — multiply before dividing.
    const surchargeTiyin = (financedCentisqm * surchargePerSqm * BigInt(plan.termMonths)) / 1_200n;
    return { baseTiyin, downTiyin, surchargeTiyin, totalTiyin: discountedBaseTiyin + surchargeTiyin };
  }

  // flat: surcharge added to every m²; default down is a percent of the gross.
  const surchargeTiyin = (surchargePerSqm * areaCentisqm) / 100n;
  const grossTiyin = baseTiyin + surchargeTiyin;
  const downTiyin = downOverrideTiyin ?? (grossTiyin * pctBp) / 10_000n;
  return { baseTiyin, downTiyin, surchargeTiyin, totalTiyin: grossTiyin - discountTiyin };
}
